import { db } from "./DBHelper";
import { COLLECTIONS, PRINTER_COMMANDS } from "../const";
import DateTimeHelpers from "./DateTimeHelpers";
import { buildShopHeader } from "./ReportHelper";
import { textToBytes, mergeBytes, padToTwoSides } from "./StringHelper";
import { roundOff } from "./NumberFormatHelper";

/**
 * Saves a deleted cart record to the local IndexedDB.
 * @param {Object} sale - The sale object being deleted.
 * @param {string} shiftId - Current shift ID.
 * @param {string} [description=""] - Reason or description for deleting the cart.
 */
export const saveDeletedCart = async (sale, shiftId, description = "") => {
  const record = {
    cartNumber: sale.local_id,
    createdAt: sale.time || null,
    deletedAt: DateTimeHelpers.getLocalISOString(),
    amount: sale.payable || 0,
    shiftId: shiftId || null,
    items: sale?.items,
    description,
  };

  try {
    await db.add(COLLECTIONS.DELETED_CARTS.name, record);
  } catch (err) {
    console.error("DeletedCartHelper#saveDeletedCart error:", err);
  }
};

/**
 * Retrieves all deleted cart records for a given shift.
 * @param {string|number} shiftId
 * @returns {Promise<Array>}
 */
export const getDeletedCartsByShift = async (shiftId) => {
  if (!shiftId) return [];
  try {
    const all = await db.getAll(COLLECTIONS.DELETED_CARTS.name);
    return all.filter((r) => String(r.shiftId) === String(shiftId));
  } catch (err) {
    console.error("DeletedCartHelper#getDeletedCartsByShift error:", err);
    return [];
  }
};

/**
 * Builds an ESC/POS byte stream for the deleted cart print slip.
 * @param {Array} records - Array of { cartNumber, createdAt, deletedAt, amount }
 * @param {Object} shop - Shop object (for currency etc.)
 * @param {Object} shift - The current shift object
 * @returns {Uint8Array}
 */

export const getDeletedCartPrintText = (records = [], shop, shift) => {
  //   const currency = shop?.currency || "";
  const lines = [];
  const lineWidth = 48;

  buildShopHeader(lines);

  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
  lines.push(textToBytes("DELETED CARTS\n"));
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);
  lines.push(PRINTER_COMMANDS.ALIGN_LEFT);

  lines.push(textToBytes(padToTwoSides("Salesperson:", "All")));
  lines.push(
    textToBytes(padToTwoSides("Shift:", shift ? `${DateTimeHelpers.getDate(shift.opened_at)}-${shift.name}` : "-")),
  );
  if (shift?.id) {
    lines.push(textToBytes(padToTwoSides("CLOUD ID:", String(shift.id))));
  }
  lines.push(textToBytes(padToTwoSides("Generated On:", DateTimeHelpers.formatDateTime(new Date()))));

  lines.push(textToBytes("=".repeat(lineWidth)));

  lines.push(
    textToBytes(
      "#" + "Created At".padStart(11) + " Cart No ".padStart(9) + "Deleted At".padStart(13) + "Amount".padStart(14),
    ),
  );
  lines.push(textToBytes("-".repeat(lineWidth)));

  let totalAmount = 0;
  records.forEach((r, idx) => {
    totalAmount += Number(r.amount || 0);

    const indexStr = String(idx + 1).padEnd(2);
    const createdAtTime = r.createdAt ? DateTimeHelpers.getLocalTime(r.createdAt) : "-";
    const deletedAtTime = r.deletedAt ? DateTimeHelpers.getLocalTime(r.deletedAt) : "-";
    const cartNo = String(r.cartNumber || "-");
    const amountStr = roundOff(r.amount || 0);

    let left = "";
    left += indexStr.padEnd(3);
    left += createdAtTime.padEnd(11);
    left += cartNo.padStart(6);
    left += deletedAtTime.padStart(16);
    left += amountStr.padStart(12);

    lines.push(textToBytes(left));
  });

  lines.push(textToBytes("-".repeat(lineWidth)));

  lines.push(textToBytes("Total Carts Deleted:".padEnd(38) + String(records.length).padStart(10)));
  lines.push(textToBytes("Deleted carts value:".padEnd(38) + String(roundOff(totalAmount)).padStart(10)));

  lines.push(PRINTER_COMMANDS.FEED_PAPER);
  lines.push(PRINTER_COMMANDS.CUT_CHARS);
  lines.push(PRINTER_COMMANDS.BEEP_SOUND);

  return mergeBytes(lines);
};

/**
 * Computes aggregate totals from a list of deleted cart records.
 * @param {Array} records
 * @returns {{ count: number, totalAmount: number }}
 */
export const computeDeletedCartTotals = (records = []) => {
  return {
    count: records.length,
    totalAmount: records.reduce((sum, r) => sum + Number(r.amount || 0), 0),
  };
};
